import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Loader2, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../api';

const PRESETS = [500, 1000, 2500, 5000];

export default function SupportPage() {
  const [amount, setAmount] = useState(2500);
  const [custom, setCustom] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function donate() {
    setErr(null); setLoading(true);
    const cents = custom ? Math.round(Number(custom) * 100) : amount;
    if (!cents || cents < 100) { setErr('Please enter at least $1.'); setLoading(false); return; }
    try {
      const { checkout_url } = await api.donate({ amount_cents: cents, recurring });
      window.location.href = checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start donation. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen"><Navbar />
      <main className="max-w-[720px] mx-auto px-6 lg:px-8 pt-10 pb-16">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#FBE6CC] text-[#A06628]">
            <Heart size={11} /> Support this project
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622]">Help keep this data free</h1>
          <p className="mt-4 text-[14px] text-[#3A4A44] leading-relaxed max-w-[560px] mx-auto">
            In order to keep this project free for everyone, every donation is greatly appreciated so we can continue to make advancements. We believe that this data <strong>needs to be free</strong> — but projects this extensive require real funding to build, host, and keep current.
          </p>
          <p className="mt-2 text-[13px] text-[#5A6660] max-w-[560px] mx-auto">Your support pays for MongoDB hosting, CMS data pipelines, and the ongoing engineering that keeps the platform up to date.</p>
        </div>

        <div className="mt-10 rounded-2xl bg-white border border-[#E5DCC4] p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setRecurring(false)} data-testid="support-toggle-onetime"
              className={`h-10 px-4 rounded-md text-[13px] font-medium ${!recurring ? 'bg-[#1A2622] text-[#FBF6EC]' : 'bg-[#F5EFDD] text-[#5A6660]'}`}>One-time</button>
            <button onClick={() => setRecurring(true)} data-testid="support-toggle-monthly"
              className={`h-10 px-4 rounded-md text-[13px] font-medium ${recurring ? 'bg-[#1A2622] text-[#FBF6EC]' : 'bg-[#F5EFDD] text-[#5A6660]'}`}>Monthly</button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PRESETS.map((c) => (
              <button key={c} onClick={() => { setAmount(c); setCustom(''); }}
                data-testid={`support-preset-${c}`}
                className={`h-14 rounded-lg border font-display text-xl tabular-nums ${(!custom && amount === c) ? 'bg-[#A06628] text-white border-[#A06628]' : 'bg-white text-[#1A2622] border-[#E5DCC4] hover:border-[#A06628]'}`}>
                ${c / 100}{recurring && <span className="text-[10.5px] ml-0.5 opacity-70">/mo</span>}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <label className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Or enter a custom amount</label>
            <div className="mt-1 flex items-center border border-[#E5DCC4] rounded-lg h-12 px-3.5 focus-within:border-[#A06628]">
              <span className="text-[#7A857F] mr-1">$</span>
              <input type="number" min="1" step="1" value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Any amount"
                data-testid="support-custom-input"
                className="flex-1 bg-transparent outline-none text-[15px] tabular-nums" />
              {recurring && <span className="text-[10.5px] text-[#7A857F] ml-2">/ month</span>}
            </div>
          </div>

          <button onClick={donate} disabled={loading} data-testid="support-donate-btn"
            className="mt-6 w-full h-12 rounded-md bg-[#A06628] text-white text-[14px] font-medium hover:bg-[#7A5230] disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting to Stripe…</> : <><Heart size={14}/> Donate ${custom || (amount / 100)}{recurring && ' / month'}</>}
          </button>
          {err && <div className="mt-3 text-[12px] text-[#A63A3A] text-center">{err}</div>}
          <div className="mt-4 text-[11px] text-[#A8B0AA] text-center leading-relaxed">Secure payment via Stripe. You&apos;ll receive an email receipt automatically. {recurring && 'Cancel anytime from your receipt link.'}</div>
        </div>

        <div className="mt-6 text-center text-[12.5px] text-[#7A857F]">
          Prefer not to donate? <Link to="/home" className="text-[#34538F] hover:underline">Continue exploring for free</Link>. Everything stays open to everyone, forever.
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function SupportThanksPage() {
  return (
    <div className="min-h-screen"><Navbar />
      <main className="max-w-[560px] mx-auto px-6 lg:px-8 pt-16 pb-16 text-center">
        <div className="w-16 h-16 rounded-full bg-[#E5F0EA] flex items-center justify-center mx-auto"><Check size={30} className="text-[#2D5F4F]" /></div>
        <h1 className="mt-4 font-display text-3xl text-[#1A2622]">Thank you 🙏</h1>
        <p className="mt-3 text-[14px] text-[#3A4A44]">Your donation directly funds MongoDB hosting, CMS data pipelines, and the ongoing engineering that keeps this platform running. A receipt is on its way to your email.</p>
        <Link to="/home" className="inline-flex mt-6 h-11 px-5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[13px] items-center hover:bg-[#2D5F4F]">Back to the platform →</Link>
      </main>
      <Footer />
    </div>
  );
}
